import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { theme as antTheme } from 'antd';
import type { ThemeConfig } from 'antd';
import { siteAPI } from '../services/api';

type ThemeMode = 'dark' | 'light';

interface ThemeTokens {
  bgBase: string;
  bgContent: string;
  bgCard: string;
  bgSider: string;
  bgHeader: string;
  borderColor: string;
  textPrimary: string;
  textSecondary: string;
  cardShadow: string;
  colorPrimary: string;
  colorAccent: string;
  colorAccentLight: string;
  colorAccentHover: string;
  headerGradient: string;
  siderBrandBg: string;
  statCardBorder: string;
  linkColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
}

interface ThemeContextType {
  mode: ThemeMode;
  themeConfig: ThemeConfig;
  tokens: ThemeTokens;
}

/* ── Deep Navy + Gold Palette ── */

const DARK_TOKENS: ThemeTokens = {
  bgBase: '#0A1628',
  bgContent: '#0F1D32',
  bgCard: 'rgba(15, 29, 50, 0.85)',
  bgSider: '#071222',
  bgHeader: 'linear-gradient(135deg, #0A1628 0%, #13315C 100%)',
  borderColor: 'rgba(197, 165, 90, 0.15)',
  textPrimary: '#E8ECF2',
  textSecondary: 'rgba(200, 210, 230, 0.65)',
  cardShadow: '0 2px 12px rgba(0, 0, 0, 0.4)',
  colorPrimary: '#1B4175',
  colorAccent: '#D4AF37',
  colorAccentLight: 'rgba(197, 165, 90, 0.12)',
  colorAccentHover: '#E5C04B',
  headerGradient: 'linear-gradient(135deg, #0A1628 0%, #13315C 100%)',
  siderBrandBg: 'linear-gradient(180deg, #071222 0%, #0B2545 100%)',
  statCardBorder: 'rgba(212, 175, 55, 0.25)',
  linkColor: '#5B9BD5',
  successColor: '#4CAF50',
  warningColor: '#FFB300',
  errorColor: '#EF5350',
};

const LIGHT_TOKENS: ThemeTokens = {
  bgBase: '#F8F9FC',
  bgContent: '#F8F9FC',
  bgCard: '#FFFFFF',
  bgSider: '#FFFFFF',
  bgHeader: 'linear-gradient(135deg, #0B2545 0%, #13315C 50%, #1B4175 100%)',
  borderColor: '#EEF1F6',
  textPrimary: '#1A2332',
  textSecondary: '#5A6577',
  cardShadow: '0 1px 3px rgba(11, 37, 69, 0.08), 0 1px 2px rgba(11, 37, 69, 0.06)',
  colorPrimary: '#0B2545',
  colorAccent: '#C5A55A',
  colorAccentLight: '#F5EFE0',
  colorAccentHover: '#D4AF37',
  headerGradient: 'linear-gradient(135deg, #0B2545 0%, #13315C 50%, #1B4175 100%)',
  siderBrandBg: 'linear-gradient(180deg, #0B2545 0%, #13315C 100%)',
  statCardBorder: 'rgba(197, 165, 90, 0.3)',
  linkColor: '#2563A6',
  successColor: '#2E7D32',
  warningColor: '#E6A817',
  errorColor: '#C62828',
};

const FONT_FAMILY = "'Inter', 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', -apple-system, BlinkMacSystemFont, sans-serif";

const DARK_THEME_CONFIG: ThemeConfig = {
  algorithm: [antTheme.darkAlgorithm, antTheme.compactAlgorithm],
  token: {
    colorPrimary: '#1B4175',
    colorBgContainer: '#0F1D32',
    colorBgElevated: '#142640',
    colorBgLayout: '#0A1628',
    colorBorder: 'rgba(197, 165, 90, 0.15)',
    colorText: '#E8ECF2',
    colorTextSecondary: 'rgba(200, 210, 230, 0.65)',
    colorLink: '#5B9BD5',
    borderRadius: 8,
    fontFamily: FONT_FAMILY,
    fontSize: 14,
    controlHeight: 36,
  },
  components: {
    Menu: { darkItemBg: 'transparent', darkItemSelectedBg: 'rgba(197, 165, 90, 0.12)', darkItemHoverBg: 'rgba(197, 165, 90, 0.06)' },
    Card: { colorBgContainer: 'rgba(15, 29, 50, 0.85)', borderRadiusLG: 12 },
    Button: { colorPrimary: '#1B4175', colorPrimaryHover: '#2563A6', borderRadius: 8 },
    Table: { headerBg: '#0F1D32', headerColor: '#E8ECF2' },
    Steps: { colorPrimary: '#D4AF37' },
    Tabs: { inkBarColor: '#D4AF37', itemSelectedColor: '#E8ECF2' },
    Input: { borderRadius: 8, activeBorderColor: '#D4AF37' },
    Select: { optionSelectedBg: 'rgba(197, 165, 90, 0.18)', optionActiveBg: 'rgba(197, 165, 90, 0.10)', colorBgElevated: '#142640' },
  },
};

const LIGHT_THEME_CONFIG: ThemeConfig = {
  algorithm: [antTheme.defaultAlgorithm, antTheme.compactAlgorithm],
  token: {
    colorPrimary: '#0B2545',
    colorBgContainer: '#FFFFFF',
    colorBgElevated: '#FFFFFF',
    colorBgLayout: '#F8F9FC',
    colorBorder: '#EEF1F6',
    colorLink: '#2563A6',
    borderRadius: 8,
    fontFamily: FONT_FAMILY,
    fontSize: 14,
    controlHeight: 36,
  },
  components: {
    Menu: { itemSelectedBg: 'rgba(197, 165, 90, 0.12)', itemSelectedColor: '#0B2545', itemHoverBg: 'rgba(11, 37, 69, 0.04)' },
    Card: { colorBgContainer: '#FFFFFF', borderRadiusLG: 12 },
    Button: { colorPrimary: '#0B2545', colorPrimaryHover: '#1B4175', borderRadius: 8 },
    Table: { headerBg: '#F8F9FC', headerColor: '#0B2545', borderColor: '#EEF1F6' },
    Steps: { colorPrimary: '#C5A55A' },
    Tabs: { inkBarColor: '#C5A55A', itemSelectedColor: '#0B2545' },
    Tag: { borderRadiusSM: 4 },
    Input: { borderRadius: 8, activeBorderColor: '#C5A55A' },
    Select: { optionSelectedBg: 'rgba(11, 37, 69, 0.08)', optionActiveBg: 'rgba(11, 37, 69, 0.04)' },
  },
};

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  themeConfig: LIGHT_THEME_CONFIG,
  tokens: LIGHT_TOKENS,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    return (localStorage.getItem('precidose_theme') as ThemeMode) || 'light';
  });

  useEffect(() => {
    siteAPI.getConfig('ui_theme')
      .then(data => {
        const serverMode: ThemeMode = data.value === 'dark' ? 'dark' : 'light';
        setMode(serverMode);
        localStorage.setItem('precidose_theme', serverMode);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = mode === 'dark' ? '#0A1628' : '#F8F9FC';
    document.body.style.colorScheme = mode === 'dark' ? 'dark' : 'light';
  }, [mode]);

  const value = useMemo<ThemeContextType>(() => ({
    mode,
    themeConfig: mode === 'dark' ? DARK_THEME_CONFIG : LIGHT_THEME_CONFIG,
    tokens: mode === 'dark' ? DARK_TOKENS : LIGHT_TOKENS,
  }), [mode]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
